import { useEffect, useRef } from 'react';

const DRAFT_VERSION = 1;

interface DraftEnvelope<T> {
  version: number;
  updatedAt: string;
  data: T;
}

export function useDraftAutosave<T>(key: string | null, value: T, enabled: boolean, debounceMs = 500) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled || !key) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      try {
        const envelope: DraftEnvelope<T> = {
          version: DRAFT_VERSION,
          updatedAt: new Date().toISOString(),
          data: value,
        };
        localStorage.setItem(key, JSON.stringify(envelope));
      } catch (e) {
        // quota exceeded or storage disabled — silent
      }
    }, debounceMs);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [key, value, enabled, debounceMs]);
}

export function loadDraft<T>(key: string): { data: T; updatedAt: string } | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DraftEnvelope<T>;
    if (!parsed || parsed.version !== DRAFT_VERSION) {
      localStorage.removeItem(key);
      return null;
    }
    return { data: parsed.data, updatedAt: parsed.updatedAt };
  } catch {
    return null;
  }
}

export function clearDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}
